import { BaseComponent } from '../core/BaseComponent.js';

/**
 * FileUpload - Handles drag-and-drop file upload functionality
 */
export class FileUpload extends BaseComponent {
    constructor(selector, options = {}) {
        super(selector, options);
    }

    init() {
        if (!super.init()) return this;
        
        this.cacheElements();
        this.setupEventListeners();
        
        return this;
    }

    cacheElements() {
        this.fileInput           = this.element.parentNode.querySelector('input[type="file"]');
        this.filePreview         = this.element.parentNode.querySelector('.file-preview');
        this.filePreviewText     = this.filePreview?.querySelector('.file-preview-text-title');
        this.fileTypeTitle       = this.filePreview?.querySelector('.filetype-text-title');
        this.removeBtn           = this.filePreview?.querySelector('.remove-file');
        // Clone the original .file-preview node so we can restore it after multi-file mode.
        this._templatePreview    = this.filePreview?.cloneNode(true) || null;
    }

    setupEventListeners() {
        // Click to trigger file input.
        this.element.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.fileInput) this.fileInput.click();
        });

        // Drag and drop events
        this.setupDragAndDrop();

        // File input change
        if (this.fileInput) {
            this.fileInput.addEventListener('change', () => this.handleFileChange());
        }

        // Remove file button
        if (this.removeBtn) {
            this.removeBtn.addEventListener('click', (e) => this.removeFile(e));
        }
    }

    setupDragAndDrop() {
        ['dragenter', 'dragover'].forEach(eventName => {
            this.element.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.element.classList.add('dragover');
            });
        });

        ['dragleave', 'drop'].forEach(eventName => {
            this.element.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.element.classList.remove('dragover');
            });
        });

        this.element.addEventListener('drop', (e) => {
            this.handleDrop(e);
        });
    }

    handleDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        this.element.classList.remove('dragover');

        if (e.dataTransfer?.files?.length > 0) {
            this.fileInput.files = e.dataTransfer.files;
            this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    handleFileChange() {
        const files = this.fileInput?.files;
        if (files && files.length > 0) {
            this.showFilePreview(Array.from(files));
        } else {
            this.hideFilePreview();
        }

        // Update submit button label when multiple files selected.
        // Skip forms with their own fixed submit label (e.g. the department
        // form's "Save Department" button is not an upload-only action).
        const form      = this.fileInput?.closest('form');
        const submitBtn = form?.querySelector('[type="submit"]');
        if (submitBtn && form?.id !== 'ds_department_form') {
            const count = files?.length || 0;
            submitBtn.textContent = count > 1
                ? `Upload ${count} Files`
                : 'Upload File';
        }
    }

    showFilePreview(files) {
        // Support both a single File object (legacy) and an array.
        if (!Array.isArray(files)) files = [files];

        if (!this.filePreview || !this._templatePreview) return;

        const parent = this.filePreview.parentNode;

        // Remove any previously rendered extra .file-preview clones.
        parent.querySelectorAll('.file-preview').forEach(el => el.remove());

        // Render one cloned .file-preview per file and insert before the submit buttons.
        const anchor = parent.querySelector('.buttons_row') || null;

        files.forEach((file, index) => {
            const clone    = this._templatePreview.cloneNode(true);
            const nameEl   = clone.querySelector('.file-preview-text-title');
            const typeEl   = clone.querySelector('.filetype-text-title');
            const removeEl = clone.querySelector('.remove-file');

            if (nameEl) nameEl.textContent = file.name;
            if (typeEl) typeEl.textContent = file.name.split('.').pop().toUpperCase();
            if (removeEl) {
                removeEl.dataset.index = index;
                removeEl.addEventListener('click', (e) => {
                    e.preventDefault();
                    const dt = new DataTransfer();
                    Array.from(this.fileInput.files)
                        .filter((_, i) => i !== index)
                        .forEach(f => dt.items.add(f));
                    this.fileInput.files = dt.files;
                    this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
                });
            }

            clone.classList.add('has-file');
            anchor ? parent.insertBefore(clone, anchor) : parent.appendChild(clone);
        });

        // Re-point this.filePreview to the first clone.
        this.filePreview = parent.querySelector('.file-preview');
        this.element.parentNode.classList.add('has-file');
    }

    hideFilePreview() {
        const parent = this.filePreview?.parentNode || this.element.parentNode;

        // Remove all rendered .file-preview clones.
        parent.querySelectorAll('.file-preview').forEach(el => el.remove());

        // Restore the original single .file-preview from the saved template.
        if (this._templatePreview) {
            const restored = this._templatePreview.cloneNode(true);
            const anchor   = parent.querySelector('.buttons_row') || null;
            anchor ? parent.insertBefore(restored, anchor) : parent.appendChild(restored);

            this.filePreview     = restored;
            this.filePreviewText = restored.querySelector('.file-preview-text-title');
            this.fileTypeTitle   = restored.querySelector('.filetype-text-title');
            this.removeBtn       = restored.querySelector('.remove-file');
            if (this.removeBtn) {
                this.removeBtn.addEventListener('click', (e) => this.removeFile(e));
            }
        }

        this.element.parentNode.classList.remove('has-file');

        // Reset submit button label (skip forms with a fixed label, see handleFileChange()).
        const form      = this.fileInput?.closest('form');
        const submitBtn = form?.querySelector('[type="submit"]');
        if (submitBtn && form?.id !== 'ds_department_form' && submitBtn.textContent.startsWith('Upload')) {
            submitBtn.textContent = 'Upload File';
        }
    }

    removeFile(e) {
        e.preventDefault();
        
        if (this.fileInput) {
            this.fileInput.value = '';
            this.hideFilePreview();
        }
    }

    /**
     * Static method to initialize all file upload containers
     */
    static initAll(selector = '.file-upload-container') {
        const containers = document.querySelectorAll(selector);
        const instances = [];

        containers.forEach(container => {
            const instance = new FileUpload(container);
            instance.init();
            instances.push(instance);
        });

        return instances;
    }
}

